/* =====================================================================
 * ThinkingBlock — 모델의 추론(thinking) 과정을 접이식으로 표시
 * ===================================================================== */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes, css } from 'styled-components';
import { Brain, ChevronDown } from 'lucide-react';

const MAX_HEIGHT = 280;

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: contents;
`;

const ToggleBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#fff' : theme.colors.textSecondary};
  padding: 5px 12px 5px 6px;
  border-radius: 20px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.025)'};
  border: 1px solid transparent;
  transition: all 0.2s ease;
  cursor: pointer;
  animation: ${fadeIn} 0.2s ease forwards;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.accent}30;
    `}
`;

const IconBadge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  border-radius: 11px;
  flex-shrink: 0;
  box-sizing: border-box;

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.mode === 'dark' ? '#111111' : theme.colors.accent};
          color: ${theme.mode === 'dark' ? theme.colors.accent : '#fff'};
          border: 1px solid ${theme.mode === 'dark' ? `${theme.colors.accent}40` : 'transparent'};
        `
      : css`
          background: ${theme.mode === 'dark'
            ? '#111111'
            : 'rgba(0,0,0,0.07)'};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'transparent'};
        `}

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  animation: ${pulse} 1.2s ease-in-out infinite;
  flex-shrink: 0;
`;

const ChevronWrap = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.6)' : theme.colors.textMuted};
`;

const Collapsible = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  order: 1;
  flex-basis: 100%;
`;

const CollapsibleInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const ContentWrap = styled.div`
  margin-top: 6px;
  padding: 10px 14px;
  border-radius: 10px;
  border-left: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'};
  max-height: ${MAX_HEIGHT}px;
  overflow-y: auto;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  }
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &:hover {
    scrollbar-color: ${({ theme }) =>
      `${theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'} transparent`};
  }
`;

const ThinkingText = styled.pre`
  font-family: inherit;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface Props {
  content: string;
  isThinking: boolean;
  hasContent?: boolean;
  isStreaming?: boolean;
}

export const ThinkingBlock: React.FC<Props> = ({ content, isThinking, hasContent, isStreaming }) => {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasAutoCollapsed = useRef(false);
  const resolvedIsThinking = isThinking || (isStreaming && !hasContent);

  /* 추론 시작 → 자동 펼침 + auto-collapse 플래그 리셋 */
  useEffect(() => {
    if (resolvedIsThinking) {
      setExpanded(true);
      hasAutoCollapsed.current = false;
    }
  }, [resolvedIsThinking]);

  /* 추론 종료 + 응답 콘텐츠 도착 → 자동 접힘 (1회만) */
  useEffect(() => {
    if (!resolvedIsThinking && hasContent && !hasAutoCollapsed.current) {
      setExpanded(false);
      hasAutoCollapsed.current = true;
    }
  }, [resolvedIsThinking, hasContent]);

  /* 스트리밍 중 자동 스크롤 */
  useEffect(() => {
    const el = contentRef.current;
    if (el && resolvedIsThinking && expanded) {
      el.scrollTop = el.scrollHeight;
    }
  }, [content, resolvedIsThinking, expanded]);

  const label = useMemo(() => {
    if (resolvedIsThinking) return t('chat.reasoning');
    return t('chat.reasoningProcess');
  }, [resolvedIsThinking, t]);

  if (!content) return null;

  return (
    <Container>
      <ToggleBtn $active={resolvedIsThinking} onClick={() => setExpanded((v) => !v)}>
        <IconBadge $active={resolvedIsThinking}>
          <Brain />
        </IconBadge>
        {label}
        {resolvedIsThinking && <ActiveDot />}
        <ChevronWrap $expanded={expanded}>
          <ChevronDown size={14} />
        </ChevronWrap>
      </ToggleBtn>

      <Collapsible $expanded={expanded}>
        <CollapsibleInner>
          <ContentWrap ref={contentRef}>
            <ThinkingText>{content}</ThinkingText>
          </ContentWrap>
        </CollapsibleInner>
      </Collapsible>
    </Container>
  );
};
